import {
  Component, Input, Output, EventEmitter, OnInit,
  inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AssetService, SpaceAvailabilityRecord } from '../../services/asset.service';
import { SpacePublicRouteService } from '../../services/space-public-route.service';
import { Space, ScheduleException } from '../../models/asset.model';
import { CALENDAR_BLOCKING_STATUSES } from '../../models/booking.model';
import { isBookableDay, bookableMinDay } from '../../shared/schedule/booking-window.utils';
import { computeDayAvailabilityStatus } from '../../shared/schedule/schedule-occupancy.utils';

// ── Day status ────────────────────────────────────────────────────────────────
//  available — sin bloqueo alguno                         → verde
//  partial   — alguna hora ocupada (provisional o parcial)→ gris
//  full      — todo el horario operativo cubierto
//              por bloques definitivos                    → rojo
//  past      — hoy o anterior (solo se reserva desde mañana)
//  padding   — celda de alineación vacía

type DayStatus = 'available' | 'partial' | 'full' | 'past' | 'padding';

// ── Calendar types ────────────────────────────────────────────────────────────

export interface CalendarDay {
  date:    string;
  dayNum:  number;
  isToday: boolean;
  status:  DayStatus;
}

export interface CalendarMonth {
  year:  number;
  month: number;
  label: string;
  days:  CalendarDay[];
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-space-calendar-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './space-calendar-modal.component.html',
})
export class SpaceCalendarModalComponent implements OnInit {
  @Input({ required: true }) space!: Space;
  @Output() closed = new EventEmitter<void>();

  private auth        = inject(Auth);
  private router      = inject(Router);
  private assetSvc    = inject(AssetService);
  private spaceRoutes = inject(SpacePublicRouteService);

  readonly loading = signal(true);
  readonly error   = signal<string | null>(null);
  readonly months  = signal<CalendarMonth[]>([]);

  readonly DOW = ['LU', 'MA', 'MI', 'JU', 'VI', 'SÁ', 'DO'] as const;

  private readonly _today           = new Date();
  private readonly _todayStr        = this._dateStr(this._today);
  /** First bookable calendar day (tomorrow) — YYYY-MM-DD. */
  private readonly _firstBookableStr = this._dateStr(bookableMinDay());

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.months.set(this._buildCalendar());
    void this._loadAvailability();
  }

  // ── Calendar skeleton ──────────────────────────────────────────────────────

  private _buildCalendar(): CalendarMonth[] {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(this._today.getFullYear(), this._today.getMonth() + i, 1);
      return this._buildMonth(d.getFullYear(), d.getMonth());
    });
  }

  private _buildMonth(year: number, month: number): CalendarMonth {
    const raw   = new Date(year, month, 1)
      .toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    const label = raw.charAt(0).toUpperCase() + raw.slice(1);
    const dow0  = new Date(year, month, 1).getDay();
    const pad   = dow0 === 0 ? 6 : dow0 - 1;
    const last  = new Date(year, month + 1, 0).getDate();
    const days: CalendarDay[] = [
      ...Array.from({ length: pad },  () =>
        ({ date: '', dayNum: 0, isToday: false, status: 'padding' as DayStatus })),
      ...Array.from({ length: last }, (_, d) => {
        const dayNum = d + 1;
        const date   = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        return { date, dayNum, isToday: date === this._todayStr,
                 status: date < this._firstBookableStr ? ('past' as DayStatus) : ('available' as DayStatus) };
      }),
    ];
    return { year, month, label, days };
  }

  // ── Data loading ───────────────────────────────────────────────────────────

  private async _loadAvailability(): Promise<void> {
    if (!this.space.id) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      // Fetch both data sources in parallel; exceptions may fail if auth is
      // required — we handle that gracefully with a fallback to [].
      const [records, exceptions] = await Promise.all([
        this.assetSvc.getAvailabilityForSpace(this.space.id),
        this.assetSvc.getScheduleExceptions(this.space.id).catch(() => [] as ScheduleException[]),
      ]);

      // Group availability records by date (blocking statuses only)
      const byDate = new Map<string, SpaceAvailabilityRecord[]>();
      for (const rec of records) {
        if (!CALENDAR_BLOCKING_STATUSES.includes(rec.status)) continue;
        const list = byDate.get(rec.date) ?? [];
        list.push(rec);
        byDate.set(rec.date, list);
      }

      // Apply to calendar days
      this.months.update(months =>
        months.map(m => ({
          ...m,
          days: m.days.map(day => {
            if (!day.date || day.status === 'past' || day.status === 'padding') return day;
            const recs = byDate.get(day.date) ?? [];
            return { ...day, status: computeDayAvailabilityStatus(this.space, day.date, recs, exceptions) };
          }),
        })),
      );
    } catch {
      this.error.set('No se pudo cargar la disponibilidad. Inténtalo de nuevo.');
    } finally {
      this.loading.set(false);
    }
  }

  private _parseDate(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }

  // ── Click → auth bridge ────────────────────────────────────────────────────

  onDayClick(day: CalendarDay): void {
    if (day.status !== 'available' && day.status !== 'partial') return;
    if (!isBookableDay(this._parseDate(day.date))) return;

    this.spaceRoutes.rememberPendingBookingSpace(this.space.id!);
    const bookingTree = this.spaceRoutes.bookingUrlTree(this.space);
    const bookingPath = this.router.serializeUrl(bookingTree);
    const returnUrl   = `${bookingPath}?date=${day.date}`;

    const user = this.auth.currentUser;
    if (user && user.emailVerified) {
      void this.router.navigateByUrl(returnUrl);
    } else if (user && !user.emailVerified) {
      void this.router.navigate(['/verificar-email']);
    } else {
      void this.router.navigate(['/login'], { queryParams: { returnUrl } });
    }
    this.closed.emit();
  }

  close(): void { this.closed.emit(); }

  private _dateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
